import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { useResolveIncident } from '@/hooks/useMutations'
import { Loading } from '@/components/ui/Loading'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatRelativeTime } from '@/lib/format'
import type { Incident, IncidentSeverity } from '@/types/api'

const SEVERITY_STYLES: Record<IncidentSeverity, { bar: string; badge: string; label: string }> = {
  critical: { bar: 'var(--color-danger)', badge: 'color-mix(in srgb, var(--color-danger) 14%, transparent)', label: '긴급' },
  severe: { bar: 'var(--color-danger)', badge: 'color-mix(in srgb, var(--color-danger) 14%, transparent)', label: '심각' },
  warning: { bar: 'var(--color-cost)', badge: 'color-mix(in srgb, var(--color-cost) 15%, transparent)', label: '주의' },
  info: { bar: 'var(--color-accent)', badge: 'color-mix(in srgb, var(--color-accent) 12%, transparent)', label: '정보' },
}

export default function IncidentsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['incidents'],
    queryFn: () => api.incidents.list(),
    select: (res) => res.data,
  })
  const resolveIncident = useResolveIncident()

  if (isLoading) return <Loading variant="rows" count={4} />

  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={<span>✅</span>}
        title="열린 인시던트가 없습니다"
        description="모든 서비스가 정상 작동 중입니다."
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      {data.map((incident: Incident) => {
        const sev = SEVERITY_STYLES[incident.severity]
        const isOpen = incident.resolvedAt === null
        return (
          <div
            key={incident.id}
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderLeft: `4px solid ${sev.bar}`,
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xs)' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-full)',
                  background: sev.badge,
                  color: sev.bar,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {sev.label}
              </span>
              {incident.autoOpened && (
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', background: 'var(--color-surface-alt)', padding: '2px 8px', borderRadius: 'var(--radius-full)' }}>
                  자동 감지
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: isOpen ? 'var(--color-danger-text)' : 'var(--color-revenue-text)', fontWeight: 600 }}>
                {isOpen ? '● 열림' : '● 해결됨'}
              </span>
              {isOpen && (
                <button
                  type="button"
                  onClick={() => resolveIncident.mutate({
                    id: incident.id,
                    body: { resolvedAt: new Date().toISOString() },
                  })}
                  disabled={resolveIncident.isPending}
                  style={{
                    marginLeft: 'var(--space-xs)',
                    padding: '3px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-revenue-text)',
                    cursor: resolveIncident.isPending ? 'not-allowed' : 'pointer',
                    opacity: resolveIncident.isPending ? 0.6 : 1,
                  }}
                >
                  {resolveIncident.isPending ? '해결 중...' : '해결'}
                </button>
              )}
            </div>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 'var(--space-xs)' }}>
              {incident.title}
            </h3>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              시작 {formatRelativeTime(incident.startedAt)}
              {incident.resolvedAt && ` · 해결 ${formatRelativeTime(incident.resolvedAt)}`}
            </div>
          </div>
        )
      })}
    </div>
  )
}
